import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { canAnywhere } from '@hrms/shared';
import { toast } from '@hrms/ui';
import { tsr } from '../../../lib/api.js';
import { generateIdempotencyKey, idempotentRequest } from '../../../lib/idempotency.js';
import { useAuthenticatedSession } from '../../../session/SessionProvider.js';
import {
  decodeShiftPlan,
  planShiftApplication,
  SHIFT_PLAN_PARAM,
  standingAssignment,
  type ExistingShift,
  type NewShiftSpec,
  type ShiftPlanEntry,
} from '../../../routes/auth/signupShifts.js';

export const SHIFTS_NOT_SET_MESSAGE =
  'Some shifts could not be set. You can change them from Attendance → Shifts.';

/** The calls this needs, all existing routes; injectable so the flow is testable without a server. */
export interface SignupShiftApi {
  listEmployees(): Promise<{ personId: string; employmentId: string; phone: string | null }[] | null>;
  listShifts(): Promise<ExistingShift[] | null>;
  createShift(spec: NewShiftSpec): Promise<string | null>;
  listAssignments(
    personId: string,
  ): Promise<{ id: string; shiftId: string; validFrom: string; validTo: string | null }[] | null>;
  updateAssignment(assignmentId: string, shiftId: string): Promise<boolean>;
  /** The employee record's own shift (profile, directory), effective-dated from `effectiveFrom`. */
  reassignShift(employmentId: string, shiftId: string, effectiveFrom: string): Promise<boolean>;
}

export const signupShiftApi: SignupShiftApi = {
  async listEmployees() {
    const res = await tsr.employees.list.query({ query: { limit: 100 } });
    return res.status === 200
      ? res.body.items.map((employee) => ({
          personId: employee.personId,
          employmentId: employee.employmentId,
          phone: employee.phone,
        }))
      : null;
  },
  async listShifts() {
    const res = await tsr.attendance.getShifts.query();
    return res.status === 200 ? res.body : null;
  },
  async createShift(spec) {
    const res = await tsr.attendance.createShift.mutate({ body: spec, ...idempotentRequest() });
    return res.status === 201 ? res.body.id : null;
  },
  async listAssignments(personId) {
    const res = await tsr.attendance.listShiftAssignments.query({
      query: { personId, status: 'all', page: 1, limit: 20 },
    });
    return res.status === 200 ? res.body.items : null;
  },
  async updateAssignment(assignmentId, shiftId) {
    const res = await tsr.attendance.updateShiftAssignment.mutate({
      params: { assignmentId },
      body: { shiftId },
      ...idempotentRequest(),
    });
    return res.status === 200;
  },
  async reassignShift(employmentId, shiftId, effectiveFrom) {
    const res = await tsr.employees.reassign.mutate({
      params: { employmentId },
      headers: { 'idempotency-key': generateIdempotencyKey() },
      body: { shiftId, effectiveFrom, reason: 'Shift chosen at sign-up' },
    });
    return res.status === 200 || res.status === 201;
  },
};

/** `YYYY-MM-DD` plus one day, as a calendar date (no time zone involved). */
export function nextDay(date: string): string {
  const day = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + 1);
  return day.toISOString().slice(0, 10);
}

/**
 * Applies the shifts picked on sign-up's "Add your team". Everyone was created on the company
 * default (General); for each person on another shift this finds them by number among the
 * employees, reuses an org shift with the same times or creates it once, and switches the person's
 * standing assignment to it — `updateShiftAssignment`, which edits a row that began today in place
 * (a new assignment from the same date would be refused as a duplicate start). Never throws:
 * returns how many people were set and how many were not.
 *
 * That edit reaches attendance only; the employee record's own shift (what the profile and the
 * directory show) changes through the effective-dated reassign, which must start after the job
 * row's first day — today, for someone just created — so it is set from tomorrow. Its standing-shift
 * sync then finds the same shift already in place and leaves attendance alone. Best effort: if it
 * fails, attendance is still right, so it does not count the person as failed.
 */
export async function applySignupShifts(
  entries: readonly ShiftPlanEntry[],
  api: SignupShiftApi = signupShiftApi,
): Promise<{ applied: number; failed: number }> {
  if (entries.length === 0) return { applied: 0, failed: 0 };
  let applied = 0;
  let failed = 0;
  try {
    const [employees, shifts] = await Promise.all([api.listEmployees(), api.listShifts()]);
    if (!employees || !shifts) return { applied: 0, failed: entries.length };
    const plan = planShiftApplication(entries, employees, shifts);
    const employmentOf = new Map(employees.map((employee) => [employee.personId, employee.employmentId]));
    failed += plan.unmatched;
    for (const group of plan.groups) {
      let shiftId = group.shiftId;
      if (!shiftId && group.create) {
        shiftId = await api.createShift(group.create).catch(() => null);
      }
      if (!shiftId) {
        failed += group.personIds.length;
        continue;
      }
      for (const personId of group.personIds) {
        try {
          const standing = standingAssignment((await api.listAssignments(personId)) ?? []);
          const set =
            !!standing &&
            (standing.shiftId === shiftId || (await api.updateAssignment(standing.id, shiftId)));
          if (!set || !standing) {
            failed += 1;
            continue;
          }
          applied += 1;
          const employmentId = employmentOf.get(personId);
          if (employmentId) {
            await api
              .reassignShift(employmentId, shiftId, nextDay(standing.validFrom))
              .catch(() => false);
          }
        } catch {
          failed += 1;
        }
      }
    }
  } catch {
    return { applied, failed: Math.max(entries.length - applied, failed) };
  }
  return { applied, failed };
}

const APPLIED_KEY = 'peopleorbit_signup_shifts_applied';
/** Plans started in this page's lifetime: a remount (or Strict Mode's second effect) never reruns. */
const startedPlans = new Set<string>();

function alreadyApplied(raw: string): boolean {
  if (startedPlans.has(raw)) return true;
  try {
    return window.sessionStorage.getItem(APPLIED_KEY) === raw;
  } catch {
    return false;
  }
}

function markApplied(raw: string) {
  startedPlans.add(raw);
  try {
    window.sessionStorage.setItem(APPLIED_KEY, raw);
  } catch {
    // Storage blocked: the in-memory guard still covers this page.
  }
}

/**
 * Home's half of the sign-up shift picks: sign-up lands on `/?invite=sms&shifts=…` (signupShifts.ts)
 * when the owner put anyone on a shift other than General. This reads the plan, takes it off the
 * address bar, applies it once in the background, and says so only when something could not be
 * set. Renders nothing; never blocks Home or the invite sheet.
 */
export function SignupShiftApplier({ api = signupShiftApi }: { api?: SignupShiftApi }) {
  const { actor } = useAuthenticatedSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(SHIFT_PLAN_PARAM);
  const apiRef = useRef(api);

  useEffect(() => {
    if (!raw) return;
    // Off the address bar first, so a reload or a shared link does not carry it again.
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(SHIFT_PLAN_PARAM);
        return next;
      },
      { replace: true },
    );
    if (alreadyApplied(raw)) return;
    markApplied(raw);
    const entries = decodeShiftPlan(raw);
    if (entries.length === 0) return;
    if (!canAnywhere(actor, 'shift:manage')) {
      toast.warning(SHIFTS_NOT_SET_MESSAGE);
      return;
    }
    void applySignupShifts(entries, apiRef.current).then(({ failed }) => {
      if (failed > 0) toast.warning(SHIFTS_NOT_SET_MESSAGE);
    });
    // Only a new plan matters; the actor is the signed-in owner throughout.
  }, [raw]);

  return null;
}
